/**
 * Einfaches Kanban für Abteilungen mit eigener Pipeline (pipeline = custom).
 * Spalten = Stages der Abteilung, Karten = Personen der Personengruppe.
 * Verschieben per Auswahl auf der Karte (PUT workflow/{userId} {stage}).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui';
import { Button, Input } from '../../components/ui';
import { departmentsApi, type DepartmentWorkflow, type WorkflowCandidate } from '../../lib/departments';
import { getThemeColors } from '../../lib/theme';

interface Props {
  slug: string;
  dark: boolean;
}

export function AbteilungWorkflowCustom({ slug, dark }: Props) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const [wf, setWf] = useState<DepartmentWorkflow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<WorkflowCandidate[]>([]);
  const [searched, setSearched] = useState(false);

  // Suche mit Debounce (ab 2 Zeichen)
  useEffect(() => {
    const q = query.trim();
    if (!addOpen || q.length < 2) { setCandidates([]); setSearched(false); return; }
    let alive = true;
    const timer = window.setTimeout(() => {
      departmentsApi.workflowCandidates(slug, q)
        .then(r => { if (alive) { setCandidates(r); setSearched(true); } })
        .catch(() => { if (alive) { setCandidates([]); setSearched(true); } });
    }, 300);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [query, addOpen, slug]);

  const addPerson = async (c: WorkflowCandidate) => {
    try {
      await departmentsApi.moveInWorkflow(slug, c.user_id, 1);
      setAddOpen(false); setQuery(''); setCandidates([]);
      await load();
    } catch {
      setError(tr('departments.workflow.add_error'));
    }
  };

  const load = useCallback(async () => {
    try {
      setWf(await departmentsApi.workflowBySlug(slug));
      setError(null);
    } catch {
      setError(tr('departments.workflow.load_error'));
    }
  }, [slug, tr]);

  useEffect(() => { setWf(null); void load(); }, [load]);

  const move = async (userId: number, stage: number) => {
    setBusyId(userId);
    try {
      await departmentsApi.moveInWorkflow(slug, userId, stage);
      await load();
    } catch {
      setError(tr('departments.workflow.move_error'));
    } finally {
      setBusyId(null);
    }
  };

  if (error && !wf) return <div style={{ color: '#b42318', fontSize: 13 }}>{error}</div>;
  if (!wf) return <div style={{ padding: 24, color: t.muted, fontSize: 13 }}>{tr('departments.page.loading')}</div>;
  if (wf.stages.length === 0) {
    return (
      <Card dark={dark} style={{ padding: 24, maxWidth: 560 }}>
        <div style={{ fontSize: 13, color: t.muted }}>{tr('departments.workflow.no_stages')}</div>
      </Card>
    );
  }

  const stages = [...wf.stages].sort((a, b) => a.position - b.position);
  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button dark={dark} variant="secondary" size="sm" onClick={() => setAddOpen(v => !v)}>
          {tr('departments.workflow.add_person')}
        </Button>
        {addOpen && (
          <div style={{ marginTop: 8, maxWidth: 360, position: 'relative' }}>
            <Input
              dark={dark}
              autoFocus
              aria-label={tr('departments.workflow.add_search')}
              placeholder={tr('departments.workflow.add_search_placeholder')}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            {candidates.length > 0 && (
              <ul aria-label={tr('departments.members.search_results')} style={{ listStyle: 'none', margin: '4px 0 0', padding: 4, background: t.card, border: `1px solid ${t.border}`, borderRadius: 10 }}>
                {candidates.map(c => (
                  <li key={c.user_id}>
                    <button
                      type="button"
                      onClick={() => void addPerson(c)}
                      style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: '7px 10px', fontSize: 13, color: t.text, fontFamily: 'Outfit, sans-serif' }}
                    >
                      {c.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {searched && candidates.length === 0 && (
              <div style={{ fontSize: 12, color: t.muted, marginTop: 6 }}>{tr('departments.workflow.add_none')}</div>
            )}
          </div>
        )}
      </div>
      {wf.items.length === 0 && (
        <div data-testid="wf-empty-hint" style={{ fontSize: 13, color: t.muted, marginBottom: 10 }}>{tr('departments.workflow.empty_hint')}</div>
      )}
      {error && <div style={{ color: '#b42318', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 14, overflowX: 'auto', alignItems: 'flex-start', paddingBottom: 8 }}>
        {stages.map(st => {
          const items = wf.items.filter(i => i.stage === st.position);
          return (
            <div
              key={st.id}
              data-testid={`wf-col-${st.position}`}
              style={{ width: 260, flexShrink: 0, background: t.cardMuted, border: `1px solid ${t.border}`, borderRadius: 14, padding: 10 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px 10px' }}>
                <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: st.color }}/>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: t.heading }}>{st.title}</span>
                <span style={{ fontSize: 12, color: t.muted }}>{items.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map(it => (
                  <Card key={it.user_id} dark={dark} style={{ padding: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: t.heading, marginBottom: 8 }}>{it.name}</div>
                    <select
                      aria-label={tr('departments.workflow.move_to')}
                      value={it.stage}
                      disabled={busyId === it.user_id}
                      onChange={e => void move(it.user_id, Number(e.target.value))}
                      style={{
                        width: '100%', border: `1px solid ${t.border}`, borderRadius: 8, padding: '6px 8px',
                        fontSize: 12, color: t.text, background: t.inputBg, fontFamily: 'Outfit, sans-serif',
                      }}
                    >
                      {stages.map(s => <option key={s.id} value={s.position}>{s.title}</option>)}
                    </select>
                  </Card>
                ))}
                {items.length === 0 && (
                  <div style={{ fontSize: 12, color: t.muted, padding: '6px 4px' }}>{tr('departments.workflow.empty_column')}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
